/**
 * Decode an inline Curve `[sub_resource]` body. Godot writes `_limits = [min_value,
 * max_value, min_domain, max_domain]` (two entries before Godot 4.3) and `_data`.
 */
import { DEFAULT_BAKE_RESOLUTION, EMPTY_CURVE } from './types';
import { settableIntOr } from '../../../parser/valueParsers';
import { storedReal } from '../../../godot/math/real.js';
import { slotTupleRegex, parseGodotFloat, allFinite } from '../../../godot/variant/number.js';
import { readSlotTuple, slotInt } from '../../../godot/variant/intSlots.js';
import { dropTrailingComma, splitTopLevel } from '../../../godot/string/string.js';
import { resizePoints } from './pointCount';
import { CurveTangentMode } from '../../../godot/scene/curve';
/**
 * Entries per point in `_data`: `Vector2(x, y)`, left tangent, right tangent, left
 * mode, right mode. The `Vector2(…)` entry means `_data` cannot split on commas.
 */
const ELEMS_PER_POINT = 5;
/**
 * Decode a Curve resource body. A malformed or absent `_data` yields a curve with
 * no points, which `sampleCurve` answers 0 for: a caller that needs "no curve at
 * all" tests `points.length`, not the sampled value.
 */
function decodeCurve(data) {
    const limits = parseFloatArray(data._limits);
    const curve = {
        points: parsePoints(data._data),
        minValue: limits?.[0] ?? EMPTY_CURVE.minValue,
        maxValue: limits?.[1] ?? EMPTY_CURVE.maxValue,
        minDomain: limits?.[2] ?? EMPTY_CURVE.minDomain,
        maxDomain: limits?.[3] ?? EMPTY_CURVE.maxDomain,
        // `set_bake_resolution` ERR_FAILs outside 1 to 1000 (`curve.cpp:526-527`).
        bakeResolution: settableIntOr(data.bake_resolution, DEFAULT_BAKE_RESOLUTION, { min: 1, max: 1000 }, 'Curve bake_resolution'),
    };
    // `point_count` is written after `_data`, and Godot's setter resizes the point
    // list to it (curve.cpp:41-57): a smaller count drops the tail, a larger one
    // pads with default points, and a negative one is refused, leaving `_data` alone.
    const declared = slotInt(data.point_count);
    if (declared !== null && declared >= 0)
        curve.points = resizePoints(curve, declared);
    return curve;
}
/** The `Curve` a resource holds, or null for no resource or one of another type. */
export function decodeCurveResource(resource) {
    return resource?.type === 'Curve' ? decodeCurve(resource.data) : null;
}
function parsePoints(value) {
    const entries = splitArrayLiteral(value);
    if (!entries || entries.length === 0 || entries.length % ELEMS_PER_POINT !== 0)
        return [];
    const points = [];
    for (let i = 0; i < entries.length; i += ELEMS_PER_POINT) {
        // One unreadable point drops the whole curve, as in the engine: `set_data`
        // validates every element before the first write to `_points` (`curve.cpp:465`,
        // write at `:477`). `point_count` then pads it back to clamped-origin defaults.
        const position = parseVector2Entry(entries[i]);
        if (!position)
            return [];
        points.push({
            position,
            leftTangent: numberOr(entries[i + 1], 0),
            rightTangent: numberOr(entries[i + 2], 0),
            leftMode: tangentMode(entries[i + 3]),
            rightMode: tangentMode(entries[i + 4]),
        });
    }
    return points;
}
/** A float as the engine stores a `real_t`, or null when the text is not one. */
function parseStoredReal(entry) {
    const parsed = parseGodotFloat(entry);
    return parsed === null ? null : storedReal(parsed);
}
/** `[a, b, c]` of plain floats, or null when the literal is absent/malformed. */
function parseFloatArray(value) {
    const entries = splitArrayLiteral(value);
    if (!entries)
        return null;
    // `parseGodotFloat`, not `parseFloat`, which reads `5abc` as 5. Finite, since a
    // non-finite limit clamps every padded point onto Infinity and scales every sample by it.
    // Each limit is a `real_t`, so it rounds to single precision before the test.
    const numbers = entries.map(parseStoredReal);
    return numbers.some((n) => n === null || !Number.isFinite(n)) ? null : numbers;
}
/**
 * Split a Godot `[…]` array literal into its top-level entries, so a nested
 * `Vector2(0, 0)` stays one entry. Null when the value is absent or not bracketed.
 */
function splitArrayLiteral(value) {
    if (value === undefined)
        return null;
    const trimmed = value.trim();
    if (!trimmed.startsWith('[') || !trimmed.endsWith(']'))
        return null;
    // `_parse_array` closes on `TK_BRACKET_CLOSE` before it demands another value
    // (variant_parser.cpp:1658-1662), so `[…, ]` holds one fewer element than the
    // commas suggest. A kept trailing comma would fail the `% 5` arity gate.
    return dropTrailingComma(splitTopLevel(trimmed.slice(1, -1)));
}
/**
 * The canonical finite grammar, not a loose `([^,]+?)` pair, which reads
 * `Vector2(8abc, 4)` as `{x: 8, y: 4}`: a control point the file does not contain.
 */
const VECTOR2_RE = slotTupleRegex('Vector2', 2);
/**
 * The position of one `_data` entry, or null when it is not a finite `Vector2`.
 * The result is tested, not only the grammar: `Vector2(0, 1e999)` matches the
 * finite pattern and reads as `Infinity`, which would reach the particle geometry.
 */
function parseVector2Entry(entry) {
    // The slot read narrows a `Vector2i` spelling's arguments to int32 before the
    // widening into the float slot, so `Vector2i(4294967295, 0)` is the point `(-1, 0)`.
    const tuple = readSlotTuple(entry, 'Vector2', VECTOR2_RE);
    if (!tuple)
        return null;
    // A `Vector2` holds floats, so a component past the float range is infinite.
    const x = storedReal(tuple.components[0]);
    const y = storedReal(tuple.components[1]);
    return allFinite([x, y]) ? { x, y } : null;
}
/**
 * One tangent slot, or `fallback` when the text is not a finite float. `inf` and
 * `nan` are a miss, not a value: `sample`'s `y + d * tangent` would hand either on
 * to the particle geometry.
 */
function numberOr(entry, fallback) {
    const num = parseStoredReal(entry);
    return num !== null && Number.isFinite(num) ? num : fallback;
}
function tangentMode(entry) {
    return slotInt(entry) === CurveTangentMode.LINEAR ? CurveTangentMode.LINEAR : CurveTangentMode.FREE;
}
//# sourceMappingURL=decode.js.map